import { createContext, useContext, useState, useEffect } from "react";
import type { Language, Translations } from "./types";
import en from "./translations/en";
import et from "./translations/et";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const translations: Record<Language, Translations> = { en, et };

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export { LanguageContext };

/**
 * Get the persisted UI language without using a hook.
 * Falls back to English if localStorage is not available — reading it throws a
 * SecurityError when storage is blocked (Safari private mode, third-party iframe).
 */
export function getLanguageStatic(): Language {
  try {
    const saved = localStorage.getItem("language");
    return saved === "en" || saved === "et" ? saved : "en";
  } catch {
    return "en";
  }
}

/**
 * Get translations for the current language without using a hook.
 * Useful for class components that cannot use hooks.
 * Falls back to English if localStorage is not available.
 */
export function getTranslationsStatic(): Translations {
  return translations[getLanguageStatic()] || translations.en;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem("language") as Language;
      return saved && (saved === "en" || saved === "et") ? saved : "en";
    } catch (error) {
      console.error("Error loading language from localStorage:", error);
      return "en";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("language", language);
    } catch (error) {
      console.error("Error saving language to localStorage:", error);
    }
    // After the store is written, so listeners reading getLanguageStatic()
    // see the new value. State outside React (MST stores holding text derived
    // from the language) re-derives on this.
    window.dispatchEvent(new CustomEvent("franklin:language-changed", { detail: language }));
  }, [language]);

  // Safe translation getter that always returns a complete Translations object
  const getTranslations = (): Translations => {
    try {
      const t = translations[language];
      if (!t) {
        console.warn(`Translations not found for language: ${language}, falling back to English`);
        return translations.en;
      }
      return t;
    } catch (error) {
      console.error("Error getting translations:", error);
      return translations.en;
    }
  };

  const value = {
    language,
    setLanguage,
    t: getTranslations(),
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    console.error("useLanguage must be used within a LanguageProvider");
    // Return a safe fallback with proper type structure
    return {
      language: "en" as Language,
      setLanguage: () => {},
      t: translations.en,
    };
  }
  return context;
}
